import { Menu } from "@base-ui/react";
import { tokens } from "@blurb/codex-react";
import { createStyles } from "@mantine/core";
import { IconV3 } from "@rpi/openapi-core";
import React from "react";
import { RpiTableAdditionalColumn } from "../rpi-core/table/RpiTable";
import { useTableCardElement } from "./RpiTableContainer";

// `popup` and `item` mirror menuStyles.ts, which four Mantine menus still consume. Keep the two in
// sync until those migrate off Mantine's Menu.
const useStyles = createStyles({
  trigger: {
    textDecoration: "none",
    padding: 0,
    border: "none",
    background: "transparent",
    cursor: "pointer",
  },
  positioner: {
    // Base UI's positioner ships no z-index; without this the popup paints under the nav and drawers.
    zIndex: 500,
  },
  popup: {
    borderRadius: tokens.radius.sm,
    background: tokens.color.semantic.bg.surfaceRaised,
    padding: tokens.spacing[2],
    boxShadow: tokens.shadow.raised,
    minWidth: 250,
  },
  item: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacing[2],
    padding: tokens.spacing[2],
    borderRadius: tokens.radius.md,
    cursor: "pointer",
    fontFamily: tokens.font.family.sans,
    fontSize: tokens.font.size.base,
    "font-weight": tokens.font.weight.normal,
    lineHeight: tokens.font.lineHeight.normal,
    color: tokens.color.semantic.text.bold,
    "&:hover, &[data-highlighted]": {
      backgroundColor: tokens.color.semantic.bg.inputHover,
    },
    "&[data-intent-danger]": {
      color: tokens.color.semantic.text.danger,
    },
  },
  // The design gives the icon its own color, a shade bolder than the label.
  itemIcon: {
    display: "flex",
    color: tokens.color.semantic.icon.bold,
    "[data-intent-danger] > &": {
      color: tokens.color.semantic.icon.danger,
    },
  },
});

interface TableRowMenuItemProps {
  icon?: React.ReactNode;
  onClick?: () => void;
  danger?: boolean;
  children: React.ReactNode;
  "data-testid"?: string;
}

function TableRowMenuItem({ icon, danger, children, ...rest }: TableRowMenuItemProps) {
  const { classes } = useStyles();
  return (
    <Menu.Item className={classes.item} data-intent-danger={danger || undefined} {...rest}>
      {icon && <span className={classes.itemIcon}>{icon}</span>}
      {children}
    </Menu.Item>
  );
}

export interface TableRowMenuProps {
  children: React.ReactNode;
}

/**
 * Table-row "More" (⋮) menu: right-aligned to the icon with a 4px gap, opening below and
 * flipping above when the table card runs out of room.
 *
 * The popup is portaled but collides against the table card, so it flips at the card's edge
 * without being clipped by its `overflow: hidden` or inheriting a stale `aria-hidden`.
 */
function TableRowMenuRoot({ children }: TableRowMenuProps) {
  const { classes } = useStyles();
  const card = useTableCardElement();
  return (
    <Menu.Root modal={false}>
      <Menu.Trigger className={classes.trigger} aria-label="Menu Actions" data-testid="table-row-menu">
        <IconV3 icon="more_vert" size={20} color={tokens.color.semantic.icon.subtle} />
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Positioner
          className={classes.positioner}
          side="bottom"
          align="end"
          sideOffset={4}
          collisionBoundary={card ?? "clipping-ancestors"}
          collisionPadding={0}
          collisionAvoidance={{ side: "flip", align: "none", fallbackAxisSide: "none" }}
        >
          <Menu.Popup className={classes.popup}>{children}</Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  );
}

export const TableRowMenu = Object.assign(TableRowMenuRoot, { Item: TableRowMenuItem });

export function createTableRowMenuColumn<T>(renderMenu: (row: T) => React.ReactNode): RpiTableAdditionalColumn<T> {
  return {
    header: "",
    maxSize: 36,
    minSize: 36,
    size: 36,
    align: "center",
    noPadding: true,
    cell: (data) => renderMenu(data.row),
  };
}
